import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAgendaAutoRefresh } from "@/hooks/useAgendaAutoRefresh";

export const SLEEP_CHANGED = "vyv:sleep-changed";
export const notifySleepChanged = () => window.dispatchEvent(new Event(SLEEP_CHANGED));
export type SleepStatus = "loading" | "ready" | "error" | "signed-out";

/** Account-bound reads shared by the active Sleep routes. */
export function useSleepResource<T>(loader: (userId: string, assertCurrent: () => void) => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [status, setStatus] = useState<SleepStatus>("loading");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [checkedAt, setCheckedAt] = useState<number | null>(null);
  const owner = useRef<string | null | undefined>(undefined);
  const generation = useRef(0);
  const request = useRef(0);
  const mounted = useRef(false);

  const refresh = useCallback(async () => {
    const ticket = ++request.current;
    let epoch = generation.current;
    const current = () => mounted.current && ticket === request.current && epoch === generation.current;
    const assertCurrent = () => { if (!current()) throw new Error("Sleep session changed"); };
    setIsRefreshing(true);
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      assertCurrent();
      if (error) throw error;
      const next = user?.id ?? null;
      if (owner.current !== next) {
        epoch = ++generation.current; owner.current = next;
        setUserId(next); setData(null); setCheckedAt(null);
      }
      if (!next) { setStatus("signed-out"); return; }
      const result = await loader(next, assertCurrent);
      assertCurrent(); setData(result); setStatus("ready"); setCheckedAt(Date.now());
    } catch (error) {
      if (current()) { console.error("[Sleep] read failed", error); setStatus("error"); }
    } finally {
      if (current()) setIsRefreshing(false);
    }
  }, [loader]);

  useEffect(() => {
    mounted.current = true;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null;
      if (!mounted.current || owner.current === next) return;
      owner.current = next; generation.current++; request.current++;
      setUserId(next); setData(null); setCheckedAt(null); setIsRefreshing(false);
      setStatus(next ? "loading" : "signed-out");
      if (next) queueMicrotask(() => { if (mounted.current) void refresh(); });
    });
    void refresh();
    const changed = () => { if (mounted.current && owner.current) void refresh(); };
    window.addEventListener(SLEEP_CHANGED, changed);
    return () => { mounted.current = false; generation.current++; request.current++; subscription.unsubscribe(); window.removeEventListener(SLEEP_CHANGED, changed); };
  }, [refresh]);

  useAgendaAutoRefresh({ checkedAt, nextEvent: null, loading: isRefreshing, error: status === "error", enabled: !!userId || status === "error", refresh });
  const identityGeneration = generation.current;
  const capture = useCallback(() => {
    const epoch = identityGeneration;
    return () => mounted.current && owner.current === userId && !!userId && generation.current === epoch;
  }, [userId, identityGeneration]);
  return { data, userId, generation: generation.current, status, isRefreshing, checkedAt, refresh, capture };
}
